/**
 * Dispatch Preview API Contract (plan 2026-10-03-1418, U7, KTD22)
 *
 * GET /internal/platform-data-sync/dispatch-preview?schoolId=&lane= —
 *   `INTERNAL_API_KEY`; read by `pnpm sj sync-health`. Takes the school's
 *   INTERNAL id, as `sj` resolves it, like the other read-only sync routes.
 *
 * One verdict per registered sync type: what the Dispatch Decision would do for
 * this school now, in the lane asked for (the scheduled tick by default), and the
 * gate that stopped it when it would not run. Read-only: the route writes no row.
 *
 * NOT a health surface (SCH-1451). A skip is a reason, not a fault, and "skipped"
 * never means "healthy". Until the cutover (U8) nothing acts on these verdicts:
 * the old scheduler still decides, and the decision runs beside it in shadow.
 *
 * `syncType` stays `z.string()`: the backend registry is the list.
 */

import { z } from 'zod'

/**
 * Who is asking. `scheduled` is the 15-minute tick; `operator` one explicit human
 * trigger; `on-demand` automatic creation for one entity; `fanout` a child created
 * by a running parent. The backend's gate table says which gate applies in which lane.
 */
export const DISPATCH_LANES = ['scheduled', 'operator', 'on-demand', 'fanout'] as const
export const DispatchLaneSchema = z.enum(DISPATCH_LANES)
export type DispatchLane = z.infer<typeof DispatchLaneSchema>

/**
 * Every reason the Dispatch Decision can give for not running a sync type.
 * `batch_limited` is the tick's admission step after the decision, not a gate.
 */
export const DISPATCH_SKIP_REASONS = [
  'token_missing',
  'school_disabled',
  'school_parked',
  'school_down',
  'not_set_up',
  'archived',
  'disabled',
  'dispatch_only',
  'not_autopiloted',
  'autopilot_off',
  'window_closed',
  'breaker_open',
  'not_stale',
  'waiting_on',
  'live_job',
  'entity_dedup',
  'batch_limited'
] as const
export const DispatchSkipReasonSchema = z.enum(DISPATCH_SKIP_REASONS)
export type DispatchSkipReason = z.infer<typeof DispatchSkipReasonSchema>

export const GetDispatchPreviewRequestSchema = z.object({
  /** The school's internal id, digits only. */
  schoolId: z.string().regex(/^\d+$/, 'schoolId must be the internal numeric id'),
  lane: DispatchLaneSchema.default('scheduled')
})
export type GetDispatchPreviewRequest = z.infer<typeof GetDispatchPreviewRequestSchema>

/**
 * A fact the decision recorded without skipping. `health_unreadable`: the school's
 * integration-health read failed, so the school gate read it as healthy (fail-open,
 * as the old school-down gate does); the verdict rests on no health read.
 */
export const DispatchPreviewNoteSchema = z.object({
  kind: z.enum(['dependency_inactive', 'early_execution', 'health_unreadable']),
  /** `dependency_inactive`: the disabled or archived dependency that satisfied the edge. */
  dependency: z.string().nullable(),
  /** `early_execution`: why quiet queues let the type run before its frequency elapsed. */
  reason: z.string().nullable()
})
export type DispatchPreviewNote = z.infer<typeof DispatchPreviewNoteSchema>

export const DispatchPreviewVerdictSchema = z.object({
  syncType: z.string(),
  outcome: z.enum(['dispatch', 'skip']),
  /** Null when the outcome is `dispatch`. */
  reason: DispatchSkipReasonSchema.nullable(),
  /** The gate-table row that stopped it, e.g. `'2b disabled'`. Null when the outcome is `dispatch`. */
  gate: z.string().nullable(),
  /** `waiting_on` only: the dependency the type waits for. */
  waitingOn: z.string().nullable(),
  notes: z.array(DispatchPreviewNoteSchema)
})
export type DispatchPreviewVerdict = z.infer<typeof DispatchPreviewVerdictSchema>

export const GetDispatchPreviewResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    /** The internal id the request named. */
    schoolId: z.string(),
    lane: DispatchLaneSchema,
    /** The instant every verdict was evaluated at (ISO 8601). */
    evaluatedAt: z.string(),
    /** Queues were quiet and early execution was on, so the staleness gate let early runs through. */
    queuesQuiet: z.boolean(),
    verdicts: z.array(DispatchPreviewVerdictSchema)
  })
})
export type GetDispatchPreviewResponse = z.infer<typeof GetDispatchPreviewResponseSchema>
